/**
 * 「問小哨」分頁：問題送到後端 Harness，畫面顯示查了哪些資料、回覆內容，以及待確認的提案。
 *
 * 小哨只會查詢。刪除檔案、取消上傳、裝置命令都只是提案，對象在建立時就固定下來，
 * 要使用者在確認卡上按「確認執行」，後端重新檢查權限與狀態後才會動。
 *
 * 送出時附上目前畫面的情境（頁面、篩選、裝置），問「這台」「這些檔案」時小哨才知道指哪裡。
 * 裝置詳情或通知帶進來的情境會固定在這次開啟期間，使用者可以按 × 改回不依畫面回答。
 *
 * API：POST /api/v1/assistant/chat、GET /api/v1/assistant/conversations/{id}、
 *      POST /api/v1/assistant/actions/{id}/approve｜reject
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUp, Check, Crosshair, Loader2, MessageSquarePlus, Search, X } from 'lucide-react';
import { ApiError, apiGet, apiPost } from '@/lib/api';
import {
  buildChatContext, contextLabel, DEVICE_ASK_PREFIX, entriesFromConversation, plainReply, quickAsksFor,
  useAssistantStatus, type ChatEntry, type CompanionAsk,
} from '@/lib/assistant';
import { Button } from '@/components/ui/button';
import type { User } from '@/types';
import type { AssistantAction, ChatContext, ChatResponse, ConversationDetail } from '@/types/assistant';

const conversationKey = (account: string) => `edgeai:companion:chat:v1:${account}`;

function readConversation(account: string) {
  try {
    const id = Number(sessionStorage.getItem(conversationKey(account)));
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch { return null; }
}

function writeConversation(account: string, id: number | null) {
  try {
    if (id) sessionStorage.setItem(conversationKey(account), String(id));
    else sessionStorage.removeItem(conversationKey(account));
  } catch { /* 無法保存時，關掉面板再打開會從新對話開始。 */ }
}

const byId = (list: AssistantAction[]) => Object.fromEntries(list.map((a) => [a.id, a]));

export function CompanionChat({ user, onNavigate, ask = null, onAskTaken }: {
  user: User; onNavigate: () => void;
  /** 其他畫面丟來、還沒送出的提問 */
  ask?: CompanionAsk | null; onAskTaken?: () => void;
}) {
  const account = user.username;
  const status = useAssistantStatus();
  const { pathname, search } = useLocation();
  const [conversationId, setConversationId] = useState(() => readConversation(account));
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [actions, setActions] = useState<Record<number, AssistantAction>>({});
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(() => readConversation(account) !== null);
  // null 表示照網址判斷；{} 表示使用者按了 ×，這次不附情境
  const [pinned, setPinned] = useState<ChatContext | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const takenAsk = useRef<CompanionAsk | null>(null);
  const ready = status.data?.ready === true;
  const pageContext = useMemo(() => buildChatContext(pathname, search), [pathname, search]);
  const context = pinned ?? pageContext;
  const focusLabel = contextLabel(context);

  // 關掉面板再打開，接回這個分頁裡的同一段對話
  useEffect(() => {
    const id = readConversation(account);
    if (id === null) return;
    let alive = true;
    apiGet<ConversationDetail>(`/api/v1/assistant/conversations/${id}`)
      .then((detail) => {
        if (!alive || !Array.isArray(detail?.messages)) return;
        setEntries(entriesFromConversation(detail));
        setActions(byId(detail.actions ?? []));
      })
      .catch(() => {
        if (!alive) return;
        writeConversation(account, null);
        setConversationId(null);
      })
      .finally(() => alive && setRestoring(false));
    return () => { alive = false; };
  }, [account]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [entries.length, sending, restoring]);

  // 等接回舊對話後再送，免得還原的紀錄蓋掉剛送出的問題
  useEffect(() => {
    // StrictMode 開發時 effect 會跑兩次，同一個提問只送一次
    if (!ask || takenAsk.current === ask || !ready || restoring || sending) return;
    takenAsk.current = ask;
    onAskTaken?.();
    if (ask.context) setPinned(ask.context);
    void send(ask.text, { context: ask.context, fresh: ask.fresh });
    // send 每次 render 都是新的函式，放進相依清單會重複送出
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ask, ready, restoring, sending]);

  async function send(text: string, opts: { context?: ChatContext; fresh?: boolean } = {}) {
    const message = text.trim();
    if (!message || sending) return;
    const key = `local-${Date.now()}`;
    const entry: ChatEntry = { key, role: 'user', text: message, steps: [], actionIds: [] };
    const ctx = opts.context ?? context;
    const currentId = opts.fresh ? null : conversationId;
    if (opts.fresh) {
      writeConversation(account, null);
      setConversationId(null);
      setActions({});
      setEntries([entry]);
    } else {
      setEntries((list) => [...list, entry]);
    }
    setDraft('');
    setError('');
    setSending(true);
    try {
      const r = await apiPost<ChatResponse>('/api/v1/assistant/chat', {
        message, conversation_id: currentId ?? undefined, context: Object.keys(ctx).length ? ctx : undefined,
      });
      setConversationId(r.conversation_id);
      writeConversation(account, r.conversation_id);
      setActions((map) => ({ ...map, ...byId(r.actions ?? []) }));
      setEntries((list) => [...list, {
        key: `run-${r.run_id}`, role: 'assistant', text: r.reply, steps: r.steps ?? [],
        actionIds: (r.actions ?? []).map((a) => a.id), failed: Boolean(r.error),
      }]);
    } catch (e) {
      // 沒送成功：把問題放回輸入框，修改或稍後再按送出就好
      setEntries((list) => list.filter((entry) => entry.key !== key));
      setDraft((current) => current || message);
      if (e instanceof ApiError && e.status === 404) {
        writeConversation(account, null);
        setConversationId(null);
        setError('原本的對話已經不在了，再按一次送出會開始新對話。');
      } else {
        setError(e instanceof Error ? e.message : '送出失敗，請再試一次');
      }
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }

  function startOver() {
    writeConversation(account, null);
    setConversationId(null);
    setEntries([]);
    setActions({});
    setError('');
    setPinned(null);
    inputRef.current?.focus();
  }

  function askAboutDevice() {
    // 輸入框已經有字就不覆蓋，只把游標移到最後
    setDraft((current) => current.trim() ? current : DEVICE_ASK_PREFIX);
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // 注音、倉頡選字時按 Enter 是確認候選字，不能當成送出
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    void send(draft);
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void send(draft);
  }

  if (status.isPending) {
    return <div className="grid flex-1 place-items-center py-10"><Loader2 aria-hidden="true" className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  if (!ready) {
    return <div className="flex-1 overflow-y-auto px-5 py-6 text-[13px] leading-relaxed">
      <p className="font-semibold">小哨還沒接上 AI 模型</p>
      {user.role === 'admin' ? <>
        <p className="mt-1 text-muted-foreground">到「小哨與 AI」填好模型網址與模型名稱，測試通過後開啟對話。摘要、通知與異常監控不需要模型，現在就能用。</p>
        <Link to="/assistant" onClick={onNavigate} className="companion-text-link">前往設定</Link>
      </> : <p className="mt-1 text-muted-foreground">請管理員接上模型後就能直接問我。摘要、通知與異常監控現在就能用。</p>}
    </div>;
  }

  const asks = quickAsksFor(context, user.role);

  return <>
    <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-4" aria-live="polite" aria-busy={sending || restoring}>
      {restoring ? <p className="flex items-center gap-2 text-[12px] text-muted-foreground"><Loader2 aria-hidden="true" size={14} className="animate-spin" />接回剛才的對話…</p>
        : entries.length === 0 ? <div>
          <p className="text-[13px] font-semibold">想查什麼？</p>
          <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
            我會先查你有權限看的資料再回答。要刪除或重開機時，我只會列出建議，你確認後才會執行。
          </p>
          <div className="mt-3 flex flex-col items-start gap-2">
            {asks.map((s) => <button key={s.label} type="button" className="chat-suggestion" onClick={() => void send(s.text)}>{s.text}</button>)}
            <button type="button" className="chat-suggestion text-muted-foreground" onClick={askAboutDevice}>檢查某一台裝置…</button>
          </div>
        </div>
          : <ol className="space-y-4">
            {entries.map((entry) => <li key={entry.key} className={entry.role === 'user' ? 'flex justify-end' : ''}>
              {entry.role === 'user' ? <p className="chat-bubble chat-bubble--user">{entry.text}</p> : <div className="min-w-0">
                {entry.steps.length > 0 && <ul className="mb-2 flex flex-wrap gap-1.5" aria-label="小哨查過的資料">
                  {entry.steps.map((step, i) => <li key={`${step.tool}-${i}`} className={`chat-step${step.ok ? '' : ' chat-step--failed'}`} title={step.summary}>
                    {step.ok ? <Search aria-hidden="true" size={12} /> : <AlertTriangle aria-hidden="true" size={12} />}{step.label}
                  </li>)}
                </ul>}
                <p className={`chat-bubble${entry.failed ? ' chat-bubble--failed' : ''}`}>{plainReply(entry.text)}</p>
                {entry.actionIds.map((id) => actions[id] && <ActionCard key={id} action={actions[id]}
                  onChange={(next) => setActions((map) => ({ ...map, [next.id]: next }))} />)}
              </div>}
            </li>)}
            {sending && <li className="chat-thinking" role="status"><span aria-hidden="true" className="chat-dots"><i /><i /><i /></span>小哨正在查資料…</li>}
          </ol>}
    </div>

    <form onSubmit={onSubmit} className="border-t border-border px-5 pb-4 pt-3">
      {error && <p className="mb-2 text-[12px] leading-relaxed text-destructive" role="alert">{error}</p>}
      {focusLabel && <div className="chat-context">
        <Crosshair aria-hidden="true" size={13} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">依目前畫面回答：{focusLabel}</span>
        <button type="button" className="chat-context-clear" aria-label="不依目前畫面回答" title="不依目前畫面回答"
          disabled={sending} onClick={() => setPinned({})}><X aria-hidden="true" size={14} /></button>
      </div>}
      {entries.length > 0 && <div className="chat-quick" role="group" aria-label="快速提問">
        {asks.map((s) => <button key={s.label} type="button" className="chat-quick-chip" title={s.text}
          disabled={sending} onClick={() => void send(s.text)}>{s.label}</button>)}
        <button type="button" className="chat-quick-chip" title="填入問句，再補上裝置 ID" onClick={askAboutDevice}>單台裝置…</button>
      </div>}
      <div className="flex items-end gap-2">
        <label htmlFor="companion-chat-input" className="sr-only">問小哨</label>
        <textarea id="companion-chat-input" ref={inputRef} rows={2} maxLength={2000} value={draft}
          onChange={(event) => setDraft(event.target.value)} onKeyDown={onKeyDown}
          placeholder="例如：哪些裝置離線超過一小時？" className="chat-input" />
        <Button type="submit" size="icon" aria-label="送出" title="送出（Enter）" disabled={sending || !draft.trim()}>
          {sending ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <ArrowUp aria-hidden="true" className="h-4 w-4" />}
        </Button>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <span>Enter 送出，Shift + Enter 換行</span>
        {entries.length > 0 && <button type="button" onClick={startOver} disabled={sending}
          className="inline-flex min-h-[32px] items-center gap-1 rounded-sm px-1.5 font-semibold hover:text-foreground disabled:opacity-50">
          <MessageSquarePlus aria-hidden="true" size={13} />新對話
        </button>}
      </div>
    </form>
  </>;
}

const STATUS_TEXT: Record<AssistantAction['status'], string> = {
  proposed: '等你確認', executed: '已執行', failed: '執行失敗', rejected: '沒有執行', expired: '已過期',
};

function resultText(action: AssistantAction) {
  const r = action.result ?? {};
  if (action.status === 'failed') return typeof r.error === 'string' ? r.error : '執行失敗，資料沒有變動。';
  if (action.status === 'rejected') return '你選擇不執行，資料沒有變動。';
  if (action.status === 'expired') return '超過 15 分鐘沒有確認，這個提案沒有執行。需要的話請再問一次小哨。';
  if (action.status !== 'executed') return '';
  if (action.action_type === 'device_command') return '命令已送出，裝置在 2 分鐘內收到就會執行；結果可到裝置頁查看。';
  const done = Number(r.done ?? 0);
  const skipped = Number(r.skipped ?? 0);
  return `完成 ${done} 筆${skipped ? `，略過 ${skipped} 筆（狀態已改變、已備份或被資料集使用）` : ''}。`;
}

function ActionCard({ action, onChange }: { action: AssistantAction; onChange: (next: AssistantAction) => void }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now);
  const proposed = action.status === 'proposed';

  useEffect(() => {
    if (!proposed) return;
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [proposed]);

  const minutesLeft = Math.ceil((new Date(action.expires_at).getTime() - now) / 60_000);
  const expired = proposed && minutesLeft <= 0;
  const destructive = action.action_type === 'media_delete';

  async function decide(kind: 'approve' | 'reject') {
    setBusy(kind);
    setError('');
    try {
      const next = await apiPost<AssistantAction>(`/api/v1/assistant/actions/${action.id}/${kind}`,
        kind === 'approve' ? { plan_hash: action.plan_hash } : undefined);
      onChange(next);
      if (kind === 'approve') void qc.invalidateQueries();
    } catch (e) {
      if (e instanceof ApiError && e.status === 410) {
        onChange({ ...action, status: 'expired' });
      } else {
        setError(e instanceof Error ? e.message : '操作失敗，請再試一次');
        // 409：可能在別的分頁處理過了，抓最新狀態回來
        if (e instanceof ApiError && e.status === 409) {
          const latest = await apiGet<AssistantAction[]>('/api/v1/assistant/actions?status=all').catch(() => []);
          const found = Array.isArray(latest) ? latest.find((a) => a.id === action.id) : undefined;
          if (found) onChange(found);
        }
      }
    } finally {
      setBusy(null);
    }
  }

  return <section className={`chat-action${destructive ? ' chat-action--danger' : ''}`} aria-label={`提案：${action.title}`}>
    <div className="flex items-start justify-between gap-2">
      <h3 className="flex min-w-0 items-start gap-1.5 text-[13px] font-semibold">
        {destructive && <AlertTriangle aria-hidden="true" size={14} className="mt-[3px] shrink-0 text-primary" />}
        <span className="min-w-0">{action.title}</span>
      </h3>
      <span className={`chat-action-status chat-action-status--${expired ? 'expired' : action.status}`}>
        {expired ? '已過期' : STATUS_TEXT[action.status]}
      </span>
    </div>
    <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">{action.summary}</p>
    {proposed && !expired ? <>
      <p className="mt-2 text-[11px] text-muted-foreground">
        {minutesLeft <= 1 ? '1 分鐘內' : `${minutesLeft} 分鐘內`}有效。執行時會重新檢查權限與狀態，已改變的對象會略過。
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        <Button size="sm" variant={destructive ? 'destructive' : 'default'} disabled={busy !== null} onClick={() => void decide('approve')}>
          {busy === 'approve' ? <Loader2 aria-hidden="true" size={14} className="animate-spin" /> : <Check aria-hidden="true" size={14} />}
          確認執行
        </Button>
        <Button size="sm" variant="secondary" disabled={busy !== null} onClick={() => void decide('reject')}>
          {busy === 'reject' ? <Loader2 aria-hidden="true" size={14} className="animate-spin" /> : <X aria-hidden="true" size={14} />}
          不要執行
        </Button>
      </div>
    </> : <p className="mt-2 text-[12px] leading-relaxed" role="status">
      {expired ? '超過 15 分鐘沒有確認，這個提案沒有執行。需要的話請再問一次小哨。' : resultText(action)}
    </p>}
    {error && <p className="mt-2 text-[12px] text-destructive" role="alert">{error}</p>}
  </section>;
}
